import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';
import { ResultSet } from '../../shared/models/result-set.model';
import { ApiService } from '../../shared/services/api.service';
import { API_BASE_URL } from '../../shared/tokens/api.tokens';
import { ExistenceChecker } from '../../shared/interfaces/existence-checker.interface';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { WaitingListApplicant, WaitingListApplicantStatus } from './models/waiting-list-applicant.model';

@Injectable({
  providedIn: 'root'
})
export class WaitingListApplicantsService extends ApiService<WaitingListApplicant> implements ExistenceChecker<string, number> {

  readonly _baseUrl: string;

  readonly searchFields = ['firstName', 'lastName', 'email'];

  constructor(@Inject(API_BASE_URL) apiBaseUrl: string, http: HttpClient, errorHandler: ErrorHandlerService) {
    super(http, errorHandler);
    this._baseUrl = `${apiBaseUrl}waiting-list-applicants/`;
  }

  override create(model: WaitingListApplicant): Observable<WaitingListApplicant> {
    model.position = null; // Ensure position is not set by client
    return super.create(model);
  }

  override update(model: WaitingListApplicant): Observable<WaitingListApplicant> {
    if(model.status !== 'Active') {
      model.position = null; // Ensure position is cleared when not active
    }

    return super.update(model);
  }

  exists(email: string, applicantId?: number) {
    let params = new HttpParams().set('email', email);
    if (applicantId !== undefined && applicantId !== null) {
      params = params.set('applicantId', applicantId.toString());
    }
    return this.http.get<boolean>(`${this._baseUrl}email-exists`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  moveUp(id: number) {
    return this.http.patch<void>(`${this._baseUrl}${id}/move-up`, {}).pipe(
      catchError(this.handleError)
    );
  }

  moveDown(id: number) {
    return this.http.patch<void>(`${this._baseUrl}${id}/move-down`, {}).pipe(
      catchError(this.handleError)
    );
  }

  convertToMember(id: number) {
    return this.http.post<void>(`${this._baseUrl}${id}/convert-to-member`, {}).pipe(
      catchError(this.handleError)
    );
  }

  getAllByStatus(statuses: WaitingListApplicantStatus[], offset: number = 0, limit: number = 10) {
    const params = this.applyStatusFilter(
      new HttpParams().set('offset', offset.toString()).set('limit', limit.toString()),
      statuses,
    );

    return this.http.get<ResultSet<WaitingListApplicant>>(this._baseUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  searchByStatus(search: string, statuses: WaitingListApplicantStatus[], offset: number = 0, limit: number = 10) {
    let params = this.constructSearchQueryString(search);
    params = params.set('offset', offset.toString()).set('limit', limit.toString());
    params = this.applyStatusFilter(params, statuses);

    return this.http.get<ResultSet<WaitingListApplicant>>(`${this._baseUrl}search?`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  private applyStatusFilter(params: HttpParams, statuses: WaitingListApplicantStatus[]): HttpParams {
    let result = params;
    statuses.forEach((status) => {
      result = result.append('status', status);
    });
    return result;
  }
}